import { ChangeEvent, FC, FormEvent, Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Form, Row, Spinner, Stack } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import CaresPageHeader from '../shell/cares-page-header';
import { listCalendarEvents, type CalendarEventRecord } from './api';
import {
  createMyScheduleRequest,
  deleteMyScheduleRequest,
  fetchMyScheduleRequests,
  fetchSwapShiftWorkspace,
  respondToSwapShiftRequest,
  type WorkflowRequestRecord,
} from '../request-workflow/api';
import { getCaresShellBootstrap } from '../shell/bootstrap';

type SwapFormState = {
  groupId: string;
  shiftEventId: string;
  coverageNote: string;
  visibility: 'private' | 'scheduling_admins';
  recipientUserIds: string[];
  targetDates: string[];
};

const defaultFormState: SwapFormState = {
  groupId: '',
  shiftEventId: '',
  coverageNote: '',
  visibility: 'scheduling_admins',
  recipientUserIds: [],
  targetDates: [],
};

const statusBadgeClass: Record<WorkflowRequestRecord['workflowStatus'], string> = {
  private: 'bg-secondary-transparent text-secondary',
  pending: 'bg-warning-transparent text-warning',
  approved: 'bg-success-transparent text-success',
  denied: 'bg-danger-transparent text-danger',
  cancelled: 'bg-dark-transparent text-dark',
};

const startOfDayIso = () => {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  return date.toISOString();
};

const endOfWindowIso = (days: number) => {
  const date = new Date();
  date.setDate(date.getDate() + days);
  date.setHours(23, 59, 59, 999);
  return date.toISOString();
};

const currentMonthKey = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

const shiftMonthKey = (monthKey: string, offset: number) => {
  const [year, month] = monthKey.split('-').map(Number);
  const next = new Date(year, month - 1 + offset, 1);
  return `${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, '0')}`;
};

const toDateInput = (value: Date) => value.toISOString().slice(0, 10);

const formatShiftDateTime = (event: CalendarEventRecord) => {
  const start = new Date(event.start);
  const end = new Date(event.end);

  if (event.allDay) {
    return `${start.toLocaleDateString()} (all day)`;
  }

  return `${start.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })} - ${end.toLocaleString([], {
    hour: 'numeric',
    minute: '2-digit',
  })}`;
};

const formatSubmittedAt = (value: string | null) => {
  if (!value) return 'Not recorded';

  return new Date(value).toLocaleString([], {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const buildSwapRequestTitle = (event: CalendarEventRecord) =>
  `Swap shift request: ${event.title || 'Scheduled shift'} on ${new Date(event.start).toLocaleDateString()}`;

const buildSwapRequestDetails = (event: CalendarEventRecord, note: string, recipientCount: number, targetDates: string[]) => {
  const lines = [
    `Shift: ${event.title || 'Scheduled shift'}`,
    `When: ${formatShiftDateTime(event)}`,
    `Location: ${event.location || 'Not specified'}`,
    `Recipients: ${recipientCount}`,
  ];

  if (targetDates.length > 0) {
    lines.push(`Target dates: ${targetDates.join(', ')}`);
  }

  if (note.trim() !== '') {
    lines.push(`Coverage note: ${note.trim()}`);
  }

  return lines.join('\n');
};

const buildMonthColumns = (monthKey: string) => {
  const [year, month] = monthKey.split('-').map(Number);
  const first = new Date(year, month - 1, 1);
  const last = new Date(year, month, 0);
  const gridStart = new Date(first);
  gridStart.setDate(first.getDate() - first.getDay());
  const gridEnd = new Date(last);
  gridEnd.setDate(last.getDate() + (6 - last.getDay()));

  const columns: Array<{ key: string; label: string; date: string; isCurrentMonth: boolean; isWeekend: boolean }> = [];
  const cursor = new Date(gridStart);
  while (cursor <= gridEnd) {
    columns.push({
      key: toDateInput(cursor),
      label: cursor.toLocaleDateString([], { weekday: 'short' }),
      date: toDateInput(cursor),
      isCurrentMonth: cursor.getMonth() === month - 1,
      isWeekend: cursor.getDay() === 0 || cursor.getDay() === 6,
    });
    cursor.setDate(cursor.getDate() + 1);
  }

  return columns;
};

const chunkColumns = <T,>(columns: T[]) => {
  const chunks: T[][] = [];
  for (let index = 0; index < columns.length; index += 7) {
    chunks.push(columns.slice(index, index + 7));
  }
  return chunks;
};

const toggleString = (current: string[], value: string) =>
  current.includes(value) ? current.filter((item) => item !== value) : [...current, value];

const formatDateLabel = (value: string) =>
  new Date(`${value}T00:00:00`).toLocaleDateString([], { month: 'short', day: 'numeric' });

type LoadState = 'loading' | 'ready' | 'error';

const SwapShiftsPage: FC = () => {
  const bootstrap = getCaresShellBootstrap();
  const [availableGroups, setAvailableGroups] = useState<Array<{ id: number; name: string; slug: string; company: { slug: string | null } }>>([]);
  const [swapRequests, setSwapRequests] = useState<WorkflowRequestRecord[]>([]);
  const [incomingRequests, setIncomingRequests] = useState<WorkflowRequestRecord[]>([]);
  const [shiftEvents, setShiftEvents] = useState<CalendarEventRecord[]>([]);
  const [peerOptions, setPeerOptions] = useState<Array<{ id: number; name: string | null; email: string | null }>>([]);
  const [formState, setFormState] = useState<SwapFormState>(defaultFormState);
  const [selectedMonth, setSelectedMonth] = useState(currentMonthKey());
  const [loadState, setLoadState] = useState<LoadState>('loading');
  const [saving, setSaving] = useState(false);
  const [deletingRequestId, setDeletingRequestId] = useState<string | null>(null);
  const [respondingRequestId, setRespondingRequestId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const hasLoadedWorkspaceRef = useRef(false);

  const loadPage = async (preferredGroupId?: string, options?: { preserveDataOnError?: boolean }) => {
    setLoadState('loading');
    setError(null);

    try {
      const [requestPayload, shiftPayload] = await Promise.all([
        fetchMyScheduleRequests(),
        listCalendarEvents({
          start: startOfDayIso(),
          end: endOfWindowIso(180),
          type: 'shift',
        }),
      ]);

      const swaps = requestPayload.requests
        .filter((record) => record.requestType === 'swap_shift')
        .sort((left, right) => new Date(right.start).getTime() - new Date(left.start).getTime());
      const upcomingShifts = shiftPayload
        .filter((event) => new Date(event.end).getTime() >= Date.now())
        .sort((left, right) => new Date(left.start).getTime() - new Date(right.start).getTime());
      const nextGroupId = preferredGroupId || formState.groupId || String(requestPayload.availableGroups[0]?.id ?? '');
      const workspacePayload = await fetchSwapShiftWorkspace(nextGroupId);

      setAvailableGroups(requestPayload.availableGroups);
      setSwapRequests(swaps);
      setShiftEvents(upcomingShifts);
      setIncomingRequests(workspacePayload.incomingRequests);
      setPeerOptions(workspacePayload.peerOptions);
      setFormState((current) => {
        const selectedShiftId = current.shiftEventId || String(upcomingShifts[0]?.id ?? '');
        const selectedShift = upcomingShifts.find((event) => String(event.id) === selectedShiftId) ?? upcomingShifts[0] ?? null;
        const defaultTargetDate = selectedShift ? selectedShift.start.slice(0, 10) : '';

        return {
          ...current,
          groupId: nextGroupId,
          shiftEventId: selectedShiftId,
          targetDates: current.targetDates.length > 0
            ? current.targetDates
            : defaultTargetDate
              ? [defaultTargetDate]
              : [],
        };
      });
      hasLoadedWorkspaceRef.current = true;
      setLoadState('ready');
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Unable to load swap-shift data.');
      if (options?.preserveDataOnError && hasLoadedWorkspaceRef.current) {
        setLoadState('ready');
        return;
      }

      hasLoadedWorkspaceRef.current = false;
      setAvailableGroups([]);
      setSwapRequests([]);
      setShiftEvents([]);
      setIncomingRequests([]);
      setPeerOptions([]);
      setLoadState('error');
    }
  };

  useEffect(() => {
    void loadPage();
  }, []);

  const selectedShift = useMemo(
    () => shiftEvents.find((event) => String(event.id) === formState.shiftEventId) ?? null,
    [formState.shiftEventId, shiftEvents]
  );

  useEffect(() => {
    if (!selectedShift) {
      return;
    }

    const selectedDate = selectedShift.start.slice(0, 10);
    setSelectedMonth(selectedShift.start.slice(0, 7));
    setFormState((current) => (
      current.targetDates.length > 0
        ? current
        : {
            ...current,
            targetDates: [selectedDate],
          }
    ));
  }, [selectedShift?.id]);

  const monthColumns = useMemo(() => buildMonthColumns(selectedMonth), [selectedMonth]);
  const weekChunks = useMemo(() => chunkColumns(monthColumns), [monthColumns]);

  const monthShiftRows = useMemo(() => {
    const rows = new Map<string, { key: string; label: string; timeLabel: string; location: string; cells: Record<string, CalendarEventRecord[]> }>();
    const visibleShifts = shiftEvents.filter((event) => event.start.slice(0, 7) === selectedMonth);

    visibleShifts.forEach((event) => {
      const start = new Date(event.start);
      const end = new Date(event.end);
      const timeLabel = event.allDay
        ? 'All day'
        : `${start.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })} - ${end.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`;
      const key = `${event.title || 'Shift'}|${timeLabel}|${event.location || ''}`;
      const existing = rows.get(key) ?? {
        key,
        label: event.title || 'Scheduled shift',
        timeLabel,
        location: event.location || 'Location not specified',
        cells: {},
      };
      existing.cells[event.start.slice(0, 10)] = [...(existing.cells[event.start.slice(0, 10)] ?? []), event];
      rows.set(key, existing);
    });

    return Array.from(rows.values());
  }, [selectedMonth, shiftEvents]);

  const handleFieldChange = (field: keyof SwapFormState) =>
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
      const value = event.target.value;
      setFormState((current) => ({
        ...current,
        [field]: value,
      }));
    };

  const handleGroupChange = async (event: ChangeEvent<HTMLSelectElement>) => {
    const nextGroupId = event.target.value;
    const previousFormState = formState;
    setFormState((current) => ({
      ...current,
      groupId: nextGroupId,
      recipientUserIds: [],
    }));

    try {
      const workspacePayload = await fetchSwapShiftWorkspace(nextGroupId);
      setIncomingRequests(workspacePayload.incomingRequests);
      setPeerOptions(workspacePayload.peerOptions);
    } catch (requestError) {
      setFormState(previousFormState);
      setError(requestError instanceof Error ? requestError.message : 'Unable to refresh swap recipients.');
    }
  };

  const handleShiftSelection = (event: CalendarEventRecord) => {
    setFormState((current) => ({
      ...current,
      shiftEventId: String(event.id),
      targetDates: [event.start.slice(0, 10)],
    }));
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!selectedShift || !formState.groupId) {
      setError('Select a department and one of your scheduled shifts before submitting a swap request.');
      return;
    }

    if (formState.recipientUserIds.length === 0) {
      setError('Choose at least one teammate to receive this swap request.');
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      await createMyScheduleRequest({
        group_id: Number(formState.groupId),
        title: buildSwapRequestTitle(selectedShift),
        details: buildSwapRequestDetails(
          selectedShift,
          formState.coverageNote,
          formState.recipientUserIds.length,
          formState.targetDates
        ),
        start: selectedShift.start,
        end: selectedShift.end,
        allDay: selectedShift.allDay,
        visibility: formState.visibility,
        request_type: 'swap_shift',
        selected_dates: formState.targetDates,
        swap_context: {
          offered_shift_event_id: selectedShift.id,
          recipient_user_ids: formState.recipientUserIds.map((value) => Number(value)),
          target_dates: formState.targetDates,
          target_note: formState.coverageNote || null,
        },
      });

      setSuccess(
        formState.visibility === 'private'
          ? 'Private swap draft saved.'
          : 'Swap request sent to the selected recipients and scheduler workflow.'
      );
      setFormState((current) => ({
        ...defaultFormState,
        groupId: current.groupId,
        shiftEventId: current.shiftEventId,
        targetDates: current.shiftEventId ? [selectedShift.start.slice(0, 10)] : [],
      }));
      await loadPage(formState.groupId, { preserveDataOnError: true });
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Unable to submit this swap request.');
    } finally {
      setSaving(false);
    }
  };

  const handleWithdraw = async (record: WorkflowRequestRecord) => {
    setDeletingRequestId(record.id);
    setError(null);
    setSuccess(null);

    try {
      await deleteMyScheduleRequest(record.id);
      setSuccess('Swap request withdrawn.');
      await loadPage(formState.groupId, { preserveDataOnError: true });
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Unable to withdraw this swap request.');
    } finally {
      setDeletingRequestId(null);
    }
  };

  const handleRecipientResponse = async (record: WorkflowRequestRecord, decision: 'accepted' | 'declined') => {
    setRespondingRequestId(record.id);
    setError(null);
    setSuccess(null);

    try {
      await respondToSwapShiftRequest(record.id, { decision });
      setSuccess(decision === 'accepted' ? 'Swap accepted.' : 'Swap declined.');
      await loadPage(formState.groupId, { preserveDataOnError: true });
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Unable to update the swap response.');
    } finally {
      setRespondingRequestId(null);
    }
  };

  const actionableRequests = useMemo(
    () => swapRequests.filter((record) => ['private', 'pending'].includes(record.workflowStatus)),
    [swapRequests]
  );

  const acceptedRequests = useMemo(
    () => swapRequests.filter((record) => record.workflowStatus === 'approved'),
    [swapRequests]
  );

  if (!bootstrap.authenticated) {
    return (
      <>
        <CaresPageHeader homepage="My Schedule" activepage="My Schedule" page="Swap Shifts" actions={<></>} />
        <Alert variant="danger" className="custom-card">
          This workflow requires an authenticated CARES session.
          <Link to="/login" className="alert-link ms-1">
            Sign in
          </Link>
          to manage shift swaps.
        </Alert>
      </>
    );
  }

  if (loadState === 'error') {
    return (
      <>
        <CaresPageHeader homepage="My Schedule" activepage="My Schedule" page="Swap Shifts" actions={<></>} />
        <Card className="custom-card">
          <Card.Body className="p-4 p-xl-5">
            <div className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-4">
              <div>
                <Badge bg="" className="bg-danger-transparent text-danger mb-3">
                  Workspace unavailable
                </Badge>
                <h2 className="mb-2">Swap Shifts could not load.</h2>
                <p className="text-muted mb-0" style={{ maxWidth: '42rem' }}>
                  The page could not load your requests, upcoming shifts, or teammate workspace. Retry the full load when the
                  scheduling service is available again.
                </p>
              </div>
              <div className="d-flex flex-wrap gap-2">
                <Button type="button" variant="primary" onClick={() => void loadPage()}>
                  Retry
                </Button>
                <Link to="/my-schedule" className="btn btn-light">
                  Return to Overview
                </Link>
              </div>
            </div>
            {error ? <Alert variant="danger" className="mb-0 mt-4">{error}</Alert> : null}
          </Card.Body>
        </Card>
      </>
    );
  }

  return (
    <>
      <CaresPageHeader homepage="My Schedule" activepage="My Schedule" page="Swap Shifts" actions={<></>} />

      <Card className="custom-card cares-page-hero">
        <Card.Body className="p-3 p-xl-4">
          <Row className="g-3 align-items-start">
            <Col xl={8}>
              <Badge bg="" className="bg-primary-transparent text-primary mb-3">
                Direct teammate workflow
              </Badge>
              <h2 className="mb-2">Select your shift, choose recipients, and route the swap from one workspace.</h2>
              <p className="text-muted mb-0" style={{ maxWidth: '56rem' }}>
                The month board mirrors the established schedule grid, but each selectable cell is one of your own future assignments.
                Pick the offered shift, set acceptable target dates, and send the request directly to teammates while keeping the
                scheduler-visible audit trail.
              </p>
            </Col>
            <Col xl={4}>
              <Row className="g-2">
                <Col sm={6}>
                  <Card className="custom-card shadow-none border mb-0">
                    <Card.Body className="p-3">
                      <p className="text-muted text-uppercase fs-12 fw-semibold mb-1">Upcoming shifts</p>
                      <h3 className="mb-0">{shiftEvents.length}</h3>
                    </Card.Body>
                  </Card>
                </Col>
                <Col sm={6}>
                  <Card className="custom-card shadow-none border mb-0">
                    <Card.Body className="p-3">
                      <p className="text-muted text-uppercase fs-12 fw-semibold mb-1">Actionable swaps</p>
                      <h3 className="mb-0">{actionableRequests.length}</h3>
                    </Card.Body>
                  </Card>
                </Col>
                <Col sm={6}>
                  <Card className="custom-card shadow-none border mb-0">
                    <Card.Body className="p-3">
                      <p className="text-muted text-uppercase fs-12 fw-semibold mb-1">Incoming offers</p>
                      <h3 className="mb-0">{incomingRequests.length}</h3>
                    </Card.Body>
                  </Card>
                </Col>
                <Col sm={6}>
                  <Card className="custom-card shadow-none border mb-0">
                    <Card.Body className="p-3">
                      <p className="text-muted text-uppercase fs-12 fw-semibold mb-1">Accepted swaps</p>
                      <h3 className="mb-0">{acceptedRequests.length}</h3>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {error ? <Alert variant="danger">{error}</Alert> : null}
      {success ? <Alert variant="success">{success}</Alert> : null}

      {loadState === 'loading' ? (
        <Card className="custom-card">
          <Card.Body className="py-5 text-center">
            <Spinner animation="border" variant="primary" />
          </Card.Body>
        </Card>
      ) : (
        <Row className="g-4">
          <Col xl={8}>
            <Stack gap={4}>
              <Card className="custom-card">
                <Card.Header className="justify-content-between align-items-center flex-wrap gap-2">
                  <div>
                    <Card.Title>Select the offered shift</Card.Title>
                    <p className="text-muted mb-0 fs-13">
                      Use the month board to choose the shift you are offering. Only your future assignments appear here.
                    </p>
                  </div>
                  <div className="d-flex gap-2">
                    <Button type="button" variant="light" size="sm" onClick={() => setSelectedMonth(shiftMonthKey(selectedMonth, -1))}>
                      Previous month
                    </Button>
                    <Button type="button" variant="light" size="sm" onClick={() => setSelectedMonth(currentMonthKey())}>
                      Current month
                    </Button>
                    <Button type="button" variant="light" size="sm" onClick={() => setSelectedMonth(shiftMonthKey(selectedMonth, 1))}>
                      Next month
                    </Button>
                  </div>
                </Card.Header>
                <Card.Body>
                  {monthShiftRows.length === 0 ? (
                    <Alert variant="info" className="mb-0">
                      No upcoming shifts are available in {selectedMonth}. Check
                      <Link to="/my-schedule/calendar" className="alert-link ms-1">
                        My Schedule
                      </Link>
                      if you expected published assignments here.
                    </Alert>
                  ) : (
                    <div className="d-flex flex-column gap-4">
                      {weekChunks.map((weekColumns) => (
                        <div key={weekColumns[0]?.key ?? 'week'} className="border rounded-3 overflow-hidden">
                          <div className="px-3 py-2 border-bottom bg-light text-muted fs-12 fw-semibold">
                            Week of {formatDateLabel(weekColumns[0]?.date ?? monthColumns[0]?.date ?? toDateInput(new Date()))}
                          </div>
                          <div className="overflow-auto">
                            <div
                              style={{
                                minWidth: 960,
                                display: 'grid',
                                gridTemplateColumns: 'minmax(220px, 240px) repeat(7, minmax(108px, 1fr))',
                              }}
                            >
                              <div className="border-bottom border-end p-3 bg-light fw-semibold">Shift</div>
                              {weekColumns.map((column) => (
                                <div
                                  key={column.key}
                                  className={`border-bottom border-end p-3 ${column.isWeekend ? 'bg-warning-transparent' : ''}`}
                                >
                                  <div className="fw-semibold">{column.label}</div>
                                  <div className="text-muted fs-12">{formatDateLabel(column.date)}</div>
                                  {!column.isCurrentMonth ? <div className="text-muted fs-11 mt-1">Outside month</div> : null}
                                </div>
                              ))}

                              {monthShiftRows.map((row) => (
                                <Fragment key={row.key}>
                                  <div key={`${row.key}-label`} className="border-bottom border-end p-3 bg-light">
                                    <div className="fw-semibold">{row.label}</div>
                                    <div className="text-muted fs-12">{row.timeLabel}</div>
                                    <div className="text-muted fs-12">{row.location}</div>
                                  </div>
                                  {weekColumns.map((column) => {
                                    const events = row.cells[column.date] ?? [];
                                    return (
                                      <div key={`${row.key}-${column.key}`} className="border-bottom border-end p-2">
                                        {events.length === 0 ? (
                                          <div className="rounded-3 p-2 h-100 bg-light text-muted fs-12">No shift</div>
                                        ) : (
                                          <Stack gap={2}>
                                            {events.map((shiftEvent) => {
                                              const selected = String(shiftEvent.id) === formState.shiftEventId;
                                              return (
                                                <button
                                                  key={shiftEvent.id}
                                                  type="button"
                                                  className={`btn text-start rounded-3 p-2 border ${selected ? 'btn-primary' : 'btn-light'}`}
                                                  onClick={() => handleShiftSelection(shiftEvent)}
                                                >
                                                  <div className="fw-semibold fs-13">{shiftEvent.title || 'Scheduled shift'}</div>
                                                  <div className={`fs-12 ${selected ? 'text-white-50' : 'text-muted'}`}>
                                                    {new Date(shiftEvent.start).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                                                  </div>
                                                </button>
                                              );
                                            })}
                                          </Stack>
                                        )}
                                      </div>
                                    );
                                  })}
                                </Fragment>
                              ))}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </Card.Body>
              </Card>

              <Card className="custom-card">
                <Card.Header className="justify-content-between flex-wrap gap-2">
                  <Card.Title>Build the swap request</Card.Title>
                  <Link to="/my-schedule/requests" className="btn btn-sm btn-primary-light">
                    Open My Requests
                  </Link>
                </Card.Header>
                <Card.Body>
                  {availableGroups.length === 0 ? (
                    <Alert variant="warning" className="mb-0">
                      No active scheduling departments are available for your account yet.
                    </Alert>
                  ) : (
                    <Form onSubmit={handleSubmit}>
                      <Stack gap={4}>
                        <Row className="g-3">
                          <Col md={6}>
                            <Form.Group>
                              <Form.Label>Scheduling department</Form.Label>
                              <Form.Select value={formState.groupId} onChange={handleGroupChange} required>
                                {availableGroups.map((group) => (
                                  <option key={group.id} value={group.id}>
                                    {group.name} ({group.company.slug ?? 'company'})
                                  </option>
                                ))}
                              </Form.Select>
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group>
                              <Form.Label>Submission mode</Form.Label>
                              <Form.Select value={formState.visibility} onChange={handleFieldChange('visibility')}>
                                <option value="scheduling_admins">Send request and keep scheduler-visible</option>
                                <option value="private">Save as private draft</option>
                              </Form.Select>
                            </Form.Group>
                          </Col>
                        </Row>

                        {selectedShift ? (
                          <Card className="border shadow-none mb-0 bg-light">
                            <Card.Body className="py-3">
                              <div className="fw-semibold">{selectedShift.title || 'Scheduled shift'}</div>
                              <div className="text-muted">{formatShiftDateTime(selectedShift)}</div>
                              <div className="text-muted">{selectedShift.location || 'Location not specified'}</div>
                            </Card.Body>
                          </Card>
                        ) : null}

                        <div>
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <Form.Label className="mb-0">Recipients</Form.Label>
                            <span className="text-muted fs-12">
                              {formState.recipientUserIds.length} selected
                            </span>
                          </div>
                          {peerOptions.length === 0 ? (
                            <Alert variant="warning" className="mb-0">
                              No active teammates are available in this scheduling department yet.
                            </Alert>
                          ) : (
                            <Row className="g-3">
                              {peerOptions.map((peer) => {
                                const checked = formState.recipientUserIds.includes(String(peer.id));

                                return (
                                  <Col md={6} key={peer.id}>
                                    <button
                                      type="button"
                                      className={`btn w-100 text-start border rounded-3 p-3 ${checked ? 'btn-primary' : 'btn-light'}`}
                                      onClick={() =>
                                        setFormState((current) => ({
                                          ...current,
                                          recipientUserIds: toggleString(current.recipientUserIds, String(peer.id)),
                                        }))
                                      }
                                    >
                                      <div className="fw-semibold">{peer.name || 'Unnamed teammate'}</div>
                                      <div className={`fs-12 ${checked ? 'text-white-50' : 'text-muted'}`}>{peer.email || 'No email recorded'}</div>
                                    </button>
                                  </Col>
                                );
                              })}
                            </Row>
                          )}
                        </div>

                        <div>
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <Form.Label className="mb-0">Acceptable target dates</Form.Label>
                            <span className="text-muted fs-12">
                              {formState.targetDates.length === 0 ? 'None selected' : formState.targetDates.map(formatDateLabel).join(', ')}
                            </span>
                          </div>
                          <div className="d-flex flex-wrap gap-2">
                            {monthColumns
                              .filter((column) => column.isCurrentMonth)
                              .map((column) => {
                                const selected = formState.targetDates.includes(column.date);

                                return (
                                  <button
                                    key={column.key}
                                    type="button"
                                    className={`btn btn-sm ${selected ? 'btn-primary' : 'btn-light border'}`}
                                    onClick={() =>
                                      setFormState((current) => ({
                                        ...current,
                                        targetDates: toggleString(current.targetDates, column.date).sort(),
                                      }))
                                    }
                                  >
                                    {formatDateLabel(column.date)}
                                  </button>
                                );
                              })}
                          </div>
                        </div>

                        <Form.Group>
                          <Form.Label>Coverage note</Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={4}
                            value={formState.coverageNote}
                            onChange={handleFieldChange('coverageNote')}
                            placeholder="State the kind of shift you want back, any date constraints, and anything the recipients or schedulers should know."
                          />
                        </Form.Group>

                        <div className="d-flex justify-content-end">
                          <Button type="submit" disabled={saving}>
                            {saving ? 'Submitting...' : 'Send swap request'}
                          </Button>
                        </div>
                      </Stack>
                    </Form>
                  )}
                </Card.Body>
              </Card>
            </Stack>
          </Col>

          <Col xl={4}>
            <Stack gap={4}>
              <Card className="custom-card">
                <Card.Header className="justify-content-between">
                  <Card.Title>Activity rail</Card.Title>
                  <Badge bg="" className="bg-info-transparent text-info">
                    {incomingRequests.length + actionableRequests.length + acceptedRequests.length} active
                  </Badge>
                </Card.Header>
                <Card.Body className="d-flex flex-column gap-3">
                  <div className="border rounded-3 p-3">
                    <p className="text-muted text-uppercase fs-12 fw-semibold mb-2">Incoming offers</p>
                    {incomingRequests.length === 0 ? (
                      <p className="text-muted mb-0">No teammate has sent you a swap request yet.</p>
                    ) : (
                      <Stack gap={3}>
                        {incomingRequests.map((record) => (
                          <div key={record.id} className="border rounded-3 p-3">
                            <div className="d-flex justify-content-between gap-2 align-items-start">
                              <div>
                                <div className="fw-semibold">{record.title}</div>
                                <div className="text-muted fs-12">{record.requester.name || 'Unknown requester'}</div>
                              </div>
                              <Badge bg="" className={statusBadgeClass[record.workflowStatus]}>
                                {record.workflowStatus}
                              </Badge>
                            </div>
                            <div className="text-muted fs-12 mt-2">
                              {record.swapContext?.offeredShift?.start
                                ? new Date(record.swapContext.offeredShift.start).toLocaleString()
                                : new Date(record.start).toLocaleString()}
                            </div>
                            <div className="text-muted fs-12">
                              Targets: {record.swapContext?.targetDates.length ? record.swapContext.targetDates.map(formatDateLabel).join(', ') : 'Not specified'}
                            </div>
                            {record.swapContext?.canCurrentUserRespond ? (
                              <div className="d-flex gap-2 mt-3">
                                <Button
                                  size="sm"
                                  variant="success"
                                  disabled={respondingRequestId === record.id}
                                  onClick={() => void handleRecipientResponse(record, 'accepted')}
                                >
                                  {respondingRequestId === record.id ? 'Saving...' : 'Accept'}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="danger-light"
                                  disabled={respondingRequestId === record.id}
                                  onClick={() => void handleRecipientResponse(record, 'declined')}
                                >
                                  {respondingRequestId === record.id ? 'Saving...' : 'Decline'}
                                </Button>
                              </div>
                            ) : (
                              <div className="text-muted fs-12 mt-2">No response action available.</div>
                            )}
                          </div>
                        ))}
                      </Stack>
                    )}
                  </div>

                  <div className="border rounded-3 p-3">
                    <p className="text-muted text-uppercase fs-12 fw-semibold mb-2">Your active requests</p>
                    {actionableRequests.length === 0 ? (
                      <p className="text-muted mb-0">No private or pending swap requests are open.</p>
                    ) : (
                      <Stack gap={3}>
                        {actionableRequests.map((record) => (
                          <div key={record.id} className="border rounded-3 p-3">
                            <div className="d-flex justify-content-between gap-2 align-items-start">
                              <div>
                                <div className="fw-semibold">{record.title}</div>
                                <div className="text-muted fs-12">{record.group.name}</div>
                              </div>
                              <Badge bg="" className={statusBadgeClass[record.workflowStatus]}>
                                {record.workflowStatus}
                              </Badge>
                            </div>
                            <div className="text-muted fs-12 mt-2">
                              {record.swapContext?.recipientCount ?? 0} recipients
                            </div>
                            <div className="text-muted fs-12">
                              Submitted {formatSubmittedAt(record.submittedAt)}
                            </div>
                            <div className="mt-3">
                              <Button
                                variant="danger-light"
                                size="sm"
                                disabled={deletingRequestId === record.id}
                                onClick={() => void handleWithdraw(record)}
                              >
                                {deletingRequestId === record.id ? 'Withdrawing...' : 'Withdraw'}
                              </Button>
                            </div>
                          </div>
                        ))}
                      </Stack>
                    )}
                  </div>

                  <div className="border rounded-3 p-3">
                    <p className="text-muted text-uppercase fs-12 fw-semibold mb-2">Accepted swaps</p>
                    {acceptedRequests.length === 0 ? (
                      <p className="text-muted mb-0">Accepted swaps will surface here after a teammate confirms.</p>
                    ) : (
                      <Stack gap={3}>
                        {acceptedRequests.map((record) => (
                          <div key={record.id} className="border rounded-3 p-3">
                            <div className="d-flex justify-content-between gap-2 align-items-start">
                              <div>
                                <div className="fw-semibold">{record.title}</div>
                                <div className="text-muted fs-12">
                                  Accepted by {record.swapContext?.acceptedBy?.name || 'recipient'}
                                </div>
                              </div>
                              <Badge bg="" className={statusBadgeClass[record.workflowStatus]}>
                                {record.workflowStatus}
                              </Badge>
                            </div>
                            <div className="text-muted fs-12 mt-2">
                              {record.swapContext?.acceptedAt ? formatSubmittedAt(record.swapContext.acceptedAt) : 'Awaiting timestamp'}
                            </div>
                          </div>
                        ))}
                      </Stack>
                    )}
                  </div>
                </Card.Body>
              </Card>
            </Stack>
          </Col>
        </Row>
      )}
    </>
  );
};

export default SwapShiftsPage;
